import _ from "lodash";
import { stringify } from "querystring";
import siteConfig from "site-config";
import { BrandProfiles as BrandProfilesSchema } from "schemas/brand/brand-profiles";
import { LinesheetLinksSchema, PageLayoutsSchema, PageLayoutSchema, PageLinksSchema } from "schemas/pages";
import CatalogSchema from "schemas/catalogs/catalog";
import { MediaArraySchema } from "schemas/media";
import { PageLayoutComponent } from "records/page";
import { MEDIA_TYPE_IMAGE, IMAGE, VIDEO, IWidgetType } from "constants/media";
import { normalize } from "normalizr";
import { Media } from "records/media";
import { Asset } from "records/asset";

// prettier-ignore
export interface IHotspot {
    coreProductId?: string;
    targetImageId?: string;
    targetUploadId?: string;
    x: number;
    y: number;
    id: string;
}

export interface ITag {
    core_product_id?: string;
    target_image_id?: string;
    target_upload_id?: string; // used to map upload_id to image_id does NOT represent property on server
    position: number[];
    id?: string;
}

export type IColumnSpan = "12" | "8" | "6" | "4" | "3";

export interface IDisplayProperties {
    columnSpan: IColumnSpan;
    linkLabel?: string;
    componentUuid?: string;
}

export interface IMediaSettings extends IDisplayProperties {
    urlType?: string;
    message?: string;
    urlLink?: string;
    urlLabel?: string;
    groups?: string[];
}

export interface ILinkBase {
    customer_groups?: string[];
    link?: string;
    message?: string;
}

export interface IComponentParams extends ILinkBase {
    display_properties: IDisplayProperties;
    customer_groups?: string[];
    widget_subtype?: IWidgetType;
}

export const LINK_MEDIA_TYPE_MEDIA = "PAGE_LAYOUT_COMPONENT_MEDIA";
export const LINK_MEDIA_TYPE_ASSET = "PAGE_LAYOUT_COMPONENT_ASSET";

export const LINK_MEDIA = 0;
export const LINK_ASSET = 1;
export type ILinkMedia = 0;
export type ILinkAsset = 1;

export interface IPageLinkParams extends ILinkBase {
    media_type?: ILinkMedia | ILinkAsset; // should be passed only while creation
    asset_id?: string;
    page_media_id?: string;
}

interface IMediaEntryProps {
    [key: string]: { url: string };
}

export interface IMediaEntry {
    id: string;
    images?: IMediaEntryProps;
    videos?: IMediaEntryProps;
    media_type: string;
}

export interface IMediaEntries {
    [id: string]: Media;
}

export interface IAssetEntities {
    [id: string]: Asset;
}

export interface ILinesheetLinkParams extends ILinkBase {
    linesheet_id?: string;
}

export interface ILinesheetLink extends ILinkBase {
    description?: string;
    name?: string;
    page_media_id?: string;
    page_id?: string;
    hidden?: boolean;
    linesheet_id?: string;
}

export interface IVideoDisplayProperties extends IDisplayProperties {
    autoPlay?: string;
    muted?: string;
}

export interface ITextDisplayProperties extends IDisplayProperties {}

export interface IRichTextProperties {
    [key: string]: any;
}

export interface IProductDisplayProperties extends IDisplayProperties {
    componentUuid: string; // uniquely identify component product is in
    hideMsrp: "true" | "false";
    hideWholesale: "true" | "false";
}

export type IPageLinkComponentParams = IPageLinkParams & IComponentParams;
export type ILinesheetLinkComponentParams = ILinesheetLinkParams & IComponentParams;
export interface IProductLinkComponentParams extends IPageLinkParams, IComponentParams {
    display_properties: IProductDisplayProperties;
}
export interface IVideoLinkComponentParams extends IPageLinkParams, IComponentParams {
    display_properties: IVideoDisplayProperties;
}

export interface ITextLinkComponentParams extends IPageLinkParams, IComponentParams {
    display_properties: ITextDisplayProperties;
    rich_text: IRichTextProperties;
}

export type ILinkComponentParams =
    | IPageLinkComponentParams
    | ILinesheetLinkComponentParams
    | IProductLinkComponentParams
    | IVideoLinkComponentParams
    | ITextLinkComponentParams;
export type IDisplayParams = IDisplayProperties | IVideoDisplayProperties | IProductDisplayProperties;

export interface ILayoutRestrictions {
    customer_groups?: string[];
}

export interface ILayoutParams extends ILayoutRestrictions {
    display_properties: IDisplayParams;
}

export const fetchPagesQuery = (brandId: string) => ({
    force: true,
    queryKey: "fetchPages",
    schema: BrandProfilesSchema,
    url: `${siteConfig.goAPI}/v1/pages/brand/${brandId}`,
});

export const fetchPageLinkQuery = (pageId: string, linkId: string) => ({
    force: true,
    schema: PageLinksSchema,
    url: `${siteConfig.goAPI}/v1/pages/${pageId}/page_links/${linkId}`,
});

export const fetchLinesheetLinkQuery = (pageId: string, linkId: string) => ({
    force: true,
    schema: LinesheetLinksSchema,
    url: `${siteConfig.goAPI}/v1/pages/${pageId}/linesheet_links/${linkId}`,
});

export const fetchCatalogQuery = (catalogId: string) => ({
    force: true,
    schema: CatalogSchema,
    url: `/api/v3.1/catalog/${catalogId}`,
});

export const fetchPageLayoutQuery = (pageId: string, layoutId: string) => ({
    force: true,
    schema: PageLayoutsSchema,
    url: `${siteConfig.goAPI}/v1/pages/${pageId}/layouts/${layoutId}`,
});

export const fetchPageLayoutsQuery = (pageId: string, options: { limit?: number; minId?: string; maxId?: string }) => {
    const { limit, maxId, minId } = options;
    const cleanEmpty = (obj) => _.pickBy(obj, (value, key) => value);

    const queries = cleanEmpty({
        limit,
        max_id: maxId,
        min_id: minId,
    });

    return {
        force: true,
        queryKey: "fetchPageLayouts",
        schema: PageLayoutsSchema,
        url: `${siteConfig.goAPI}/v1/pages/${pageId}/layouts?${stringify(queries)}`,
    };
};

export const mutateLinkQuery = (data: IPageLinkParams, pageId: string, linkId?: string) => {
    let body;
    // HACK, link is required but we dont require it on the frontend
    if ((Object.keys(data) as any).includes("link") && !data.link) {
        data.link = "#";
    }
    if (linkId) {
        body = {
            page_link: data,
            update_mask: {
                paths: Object.keys(data),
            },
        };
    } else {
        body = data;
    }
    return {
        body,
        force: true,
        optimisticUpdate: {
            pageLinks: (prevLinks = {}) => {
                const link = prevLinks[linkId || ""];
                if (!link || !linkId) {
                    return prevLinks;
                }
                return {
                    ...prevLinks,
                    [linkId]: link.merge(_.omit(data, ["asset_id", "page_media_id"])), // omit external data params
                };
            },
        },
        options: {
            method: linkId ? "PATCH" : "POST",
        },
        schema: PageLinksSchema,
        url: `${siteConfig.goAPI}/v1/pages/${pageId}/page_links${linkId ? `/${linkId}` : ""}`,
    };
};

export const mutateLinesheetLinkQuery = (data: ILinesheetLink, pageId: string, linkId?: string) => {
    let body;
    // HACK, link is required but we dont require it on the frontend
    if ((Object.keys(data) as any).includes("link") && !data.link) {
        data.link = "#";
    }
    if (linkId) {
        body = {
            linesheet_link: data,
            update_mask: {
                paths: Object.keys(data),
            },
        };
    } else {
        body = data;
    }
    return {
        body,
        force: true,
        optimisticUpdate: {
            linesheetLinks: (prevLinks = {}) => {
                const link = prevLinks[linkId || ""];
                if (!link || !linkId) {
                    return prevLinks;
                }
                return {
                    ...prevLinks,
                    [linkId]: link.merge(data),
                };
            },
        },
        options: {
            method: linkId ? "PATCH" : "POST",
        },
        schema: LinesheetLinksSchema,
        url: `${siteConfig.goAPI}/v1/pages/${pageId}/linesheet_links${linkId ? `/${linkId}` : ""}`,
    };
};

export const mutatePageAssetsQuery = (data: any, pageId: string) => {
    const body = {
        hidden: true,
        ...data,
    };
    return {
        body,
        force: true,
        options: {
            method: "POST",
        },
        // schema: MediaAssetsArraySchema,
        url: `${siteConfig.goAPI}/v1/pages/${pageId}/assets`,
    };
};

export const mutatePageLayoutQuery = (data: any, pageId: string, layoutId?: string) => {
    let body;
    let normalizedData;
    if (data.components) {
        data.components = data.components.filter((component) => ![undefined, null].includes(component));
    }
    if (layoutId) {
        normalizedData = normalize({ ...data, id: layoutId }, PageLayoutSchema);
        body = {
            page_id: pageId, // remove when fixed on server
            page_layout: data,
            page_layout_id: layoutId, // remove when fixed on server
            update_mask: {
                paths: Object.keys(data),
            },
        };
    } else {
        body = data;
    }
    return {
        body,
        force: true,
        optimisticUpdate: {
            pageLayoutComponents: (prevComponents = {}) => {
                if (!normalizedData || !normalizedData.entities.pageLayoutComponents) {
                    return prevComponents;
                }
                const newComponents = {};
                Object.keys(normalizedData.entities.pageLayoutComponents).forEach((id) => {
                    if (prevComponents[id]) {
                        newComponents[id] = prevComponents[id].merge(normalizedData.entities.pageLayoutComponents[id]);
                    } else {
                        newComponents[id] = new PageLayoutComponent(normalizedData.entities.pageLayoutComponents[id]);
                    }
                });
                return {
                    ...prevComponents,
                    ...newComponents,
                };
            },
            pageLayouts: (prevLayouts = {}) => {
                const layout = prevLayouts[layoutId || ""];
                if (!layoutId || !layout || !normalizedData) {
                    return prevLayouts;
                }
                return {
                    ...prevLayouts,
                    [layoutId]: layout.merge(normalizedData.entities.pageLayouts[layoutId]),
                };
            },
        },
        options: {
            method: layoutId ? "PATCH" : "POST",
        },
        schema: PageLayoutsSchema,
        url: `${siteConfig.goAPI}/v1/pages/${pageId}/layouts${layoutId ? `/${layoutId}` : ""}`,
    };
};

export const deletePageLayoutQuery = (pageId: string, layoutId: string) => ({
    url: `${siteConfig.goAPI}/v1/pages/${pageId}/layouts/${layoutId}`,
    options: { method: "DELETE" },
    force: true,
});

export interface IMediaQueryParams {
    hidden?: boolean;
    media_type?: IMAGE | VIDEO;
    upload_id: string;
}

/**
 * @deprecated: Replaced by PageAssets. Should be used for backward compatibility only
 */
export const mutatePageMediaQuery = (data: IMediaQueryParams, pageId: string) => {
    const body = {
        hidden: true,
        media_type: MEDIA_TYPE_IMAGE,
        ...data,
    };
    return {
        body,
        force: true,
        options: {
            method: "POST",
        },
        schema: MediaArraySchema,
        url: `${siteConfig.goAPI}/v1/pages/${pageId}/media`,
    };
};

/**
 * @deprecated: Replaced by PageAssets. Should be used for backward compatibility only
 */
export const deletePageMediaQuery = (pageId: string, mediaId: string) => ({
    force: true,
    options: {
        method: "DELETE",
    },
    url: `${siteConfig.goAPI}/v1/pages/${pageId}/media/${mediaId}`,
});
